import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  Brain, TrendingUp, TrendingDown, AlertTriangle, Lightbulb, 
  BarChart3, Target, ArrowRight, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  FunnelChart, Funnel, LabelList, Cell
} from "recharts";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { stripMarkdown } from "../utils/stripMarkdown";

const IntelligencePage = () => {
  const [sourceQuality, setSourceQuality] = useState(null);
  const [dropoffAnalysis, setDropoffAnalysis] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchIntelligenceData();
  }, []);

  const fetchIntelligenceData = async () => {
    try {
      const [sourceRes, dropoffRes] = await Promise.all([
        axios.get(`${API}/ai/source-quality`),
        axios.get(`${API}/ai/dropoff-analysis`)
      ]);
      setSourceQuality(sourceRes.data);
      setDropoffAnalysis(dropoffRes.data);
    } catch (error) {
      console.error("Intelligence fetch error:", error);
      toast.error("Failed to fetch intelligence data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchIntelligenceData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const fetchCampaigns = async () => {
    setCampaignLoading(true);
    try {
      const res = await axios.get(`${API}/ai/campaign-recommendations`);
      setCampaigns(res.data);
    } catch (error) {
      console.error("Campaign recommendations error:", error);
    } finally {
      setCampaignLoading(false);
    }
  };

  const COLORS = ['#0F172A', '#1E293B', '#334155', '#475569', '#64748B', '#94A3B8'];

  const getQualityColor = (score) => {
    if (score >= 70) return 'text-emerald-600 bg-emerald-50';
    if (score >= 40) return 'text-amber-600 bg-amber-50';
    return 'text-red-600 bg-red-50';
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6 space-y-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="intelligence-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
              <Brain className="w-8 h-8 text-amber-500" />
              AI Intelligence
            </h1>
            <p className="text-slate-500 mt-1">Source quality analysis and drop-off insights</p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh Analysis
          </Button>
        </div>

        <Tabs defaultValue="source-quality" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="source-quality" data-testid="source-quality-tab">
              <Target className="w-4 h-4 mr-2" />
              Source Quality
            </TabsTrigger>
            <TabsTrigger value="dropoff" data-testid="dropoff-tab">
              <TrendingDown className="w-4 h-4 mr-2" />
              Drop-off
            </TabsTrigger>
            <TabsTrigger value="campaigns" data-testid="campaigns-tab" onClick={() => !campaigns && fetchCampaigns()}>
              <Lightbulb className="w-4 h-4 mr-2" />
              Campaigns
            </TabsTrigger>
          </TabsList>

          {/* Source Quality Tab */}
          <TabsContent value="source-quality" className="mt-6 space-y-6">
            {/* AI Insights Card */}
            {sourceQuality?.ai_insights && (
              <Card className="bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-amber-600" />
                    AI Insights
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-amber-800 mb-2">Key Insights:</p>
                      <ul className="space-y-1">
                        {sourceQuality.ai_insights.insights?.map((insight, idx) => (
                          <li key={idx} className="text-sm text-amber-700 flex items-start gap-2">
                            <span className="text-amber-500">•</span>
                            {insight}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      <div className="bg-white/50 rounded-lg p-3">
                        <p className="text-xs font-medium text-amber-600 uppercase">Focus On</p>
                        <p className="text-sm text-amber-900 font-medium capitalize">
                          {sourceQuality.ai_insights.focus_sources?.join(', ') || 'N/A'}
                        </p>
                      </div>
                      <div className="bg-white/50 rounded-lg p-3">
                        <p className="text-xs font-medium text-amber-600 uppercase">Reduce Spend</p>
                        <p className="text-sm text-amber-900 font-medium capitalize">
                          {sourceQuality.ai_insights.reduce_sources?.join(', ') || 'N/A'}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-amber-700 bg-white/30 p-2 rounded">
                      <strong>Budget Recommendation:</strong> {sourceQuality.ai_insights.budget_recommendation}
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Source Quality Chart */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">Source Quality Scores</CardTitle>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  Quality Score (0-100) is a weighted composite: <span className="font-semibold text-slate-700">40% Conversion Rate</span> (leads that booked) + <span className="font-semibold text-slate-700">30% Site Visit Rate</span> (leads reaching site visit or beyond) + <span className="font-semibold text-slate-700">20% Hot Lead Rate</span> (% of leads scored hot) + <span className="font-semibold text-slate-700">10% Retention</span> (inverse of loss rate). Higher = better ROI from that source.
                </p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={sourceQuality?.sources || []} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis dataKey="source" type="category" width={100} tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #E2E8F0',
                        borderRadius: '8px'
                      }}
                      formatter={(value, name) => [
                        name === 'quality_score' ? `${value}%` : value,
                        name === 'quality_score' ? 'Quality Score' : name
                      ]}
                    />
                    <Bar dataKey="quality_score" fill="#D97706" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Source Details Table */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">Source Performance Details</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Breakdown of each metric that feeds into the quality score. Click column headers to understand each metric.</p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-slate-50">
                        <th className="text-left py-3 px-4 font-medium text-slate-600">Source</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600" title="Total leads received from this source">Leads</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600" title="% of leads that reached 'booked' stage (weight: 40%)">Conversion</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600" title="% of leads that reached site visit stage or beyond (weight: 30%)">Site Visit Rate</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600" title="% of leads scored as 'hot' (weight: 20%)">Hot Lead %</th>
                        <th className="text-center py-3 px-4 font-medium text-slate-600" title="Composite score: 40% conversion + 30% site visits + 20% hot leads + 10% retention">Quality Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sourceQuality?.sources?.map((source) => (
                        <tr key={source.source} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-3 px-4 font-medium text-slate-900 capitalize">{source.source?.replace(/_/g, ' ')}</td>
                          <td className="py-3 px-4 text-center">{source.total_leads}</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${source.conversion_rate >= 10 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                              {source.conversion_rate}%
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center">{source.site_visit_rate}%</td>
                          <td className="py-3 px-4 text-center">{source.hot_lead_rate}%</td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${getQualityColor(source.quality_score)}`}>
                              {source.quality_score}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Drop-off Analysis Tab */}
          <TabsContent value="dropoff" className="mt-6 space-y-6">
            {/* AI Analysis Card */}
            {dropoffAnalysis?.ai_analysis && (
              <Card className="bg-gradient-to-r from-red-50 to-amber-50 border-red-200">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Drop-off Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-white/50 rounded-lg p-3">
                      <p className="text-xs font-medium text-red-600 uppercase">Bottleneck Stage</p>
                      <p className="text-lg text-red-900 font-bold capitalize">
                        {dropoffAnalysis.ai_analysis.bottleneck_stage?.replace(/_/g, ' ')}
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-sm font-medium text-red-800 mb-2">Top Drop-off Reasons:</p>
                      <ul className="space-y-1">
                        {dropoffAnalysis.ai_analysis.dropoff_reasons?.map((reason, idx) => (
                          <li key={idx} className="text-sm text-red-700 flex items-start gap-2">
                            <span className="text-red-500">{idx + 1}.</span>
                            {reason}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-amber-800 mb-2">Improvement Actions:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {Object.entries(dropoffAnalysis.ai_analysis.improvement_actions || {}).map(([stage, action]) => (
                          <div key={stage} className="bg-white/50 rounded-lg p-2">
                            <p className="text-xs font-medium text-amber-600 uppercase">{stage.replace(/_/g, ' ')}</p>
                            <p className="text-sm text-amber-900">{action}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Funnel Visualization */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">Sales Funnel</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  <span className="font-semibold text-slate-700">Progression Rate</span> = % of leads that moved forward from this stage. <span className="font-semibold text-slate-700">Drop-off Risk</span> = % of leads that stalled or were lost at this stage. High risk stages need immediate attention.
                </p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {dropoffAnalysis?.funnel?.map((stage, idx) => (
                    <div key={stage.stage} className="relative">
                      <div className="flex items-center gap-4">
                        <div className="w-32 text-right">
                          <p className="font-medium text-slate-900 capitalize">{stage.stage.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-slate-500">{stage.leads_at_stage} leads</p>
                        </div>
                        <div className="flex-1">
                          <div className="h-10 bg-slate-100 rounded-lg overflow-hidden relative">
                            <div 
                              className="h-full bg-gradient-to-r from-slate-700 to-slate-500 rounded-lg flex items-center justify-end pr-3"
                              style={{ width: `${Math.max(stage.progression_rate, 10)}%` }}
                            >
                              <span className="text-white text-sm font-medium">{stage.progression_rate}%</span>
                            </div>
                          </div>
                        </div>
                        <div className="w-24">
                          <span className={`text-sm font-medium ${stage.dropoff_risk > 60 ? 'text-red-600' : stage.dropoff_risk > 30 ? 'text-amber-600' : 'text-emerald-600'}`}>
                            {stage.dropoff_risk}% risk
                          </span>
                        </div>
                      </div>
                      {idx < (dropoffAnalysis?.funnel?.length || 0) - 1 && (
                        <div className="absolute left-[8.5rem] top-full h-4 flex items-center">
                          <ArrowRight className="w-4 h-4 text-slate-300" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Stage Counts */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold font-['Manrope']">Current Stage Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                  {['new', 'contacted', 'qualified', 'site_visit_scheduled', 'site_visit', 'revisit', 'negotiation_scheduled', 'negotiation', 'booked', 're_engaged', 'lost'].map((stage) => (
                    <div 
                      key={stage} 
                      className={`p-4 rounded-xl text-center ${stage === 'booked' ? 'bg-emerald-50' : stage === 'lost' ? 'bg-red-50' : 'bg-slate-50'}`}
                    >
                      <p className="text-2xl font-bold font-['Manrope']">
                        {dropoffAnalysis?.stage_counts?.[stage] || 0}
                      </p>
                      <p className="text-xs text-slate-600 capitalize mt-1">{stage.replace(/_/g, ' ')}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Campaigns Tab */}
          <TabsContent value="campaigns" className="mt-6 space-y-6">
            {campaignLoading && !campaigns ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-12 text-center text-slate-500">
                  Generating campaign recommendations...
                </CardContent>
              </Card>
            ) : campaigns ? (
              <>
                {/* Key insight */}
                {campaigns.key_insight && (
                  <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                    <CardContent className="p-4 flex items-start gap-3">
                      <Lightbulb className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                      <p className="text-sm font-medium text-amber-800">{campaigns.key_insight}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Campaign recommendations */}
                <Card className="bg-white border-slate-200 shadow-sm">
                  <CardHeader>
                    <CardTitle className="text-lg font-semibold font-['Manrope']">Recommended Campaigns</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {campaigns.campaigns?.map((c, i) => (
                        <div key={i} className="p-4 border border-slate-200 rounded-xl hover:border-slate-300 transition-colors" data-testid={`campaign-${i}`}>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-slate-900">{c.name}</h3>
                                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                  c.budget_allocation === 'high' ? 'bg-red-100 text-red-700' :
                                  c.budget_allocation === 'medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {c.budget_allocation} priority
                                </span>
                              </div>
                              <p className="text-sm text-slate-500 mt-1">Channel: <span className="font-medium text-slate-700">{c.channel}</span></p>
                              <p className="text-sm text-slate-500">Target: {c.target_audience}</p>
                              {c.expected_impact && <p className="text-sm text-emerald-600 mt-1">Impact: {c.expected_impact}</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Budget shifts */}
                {campaigns.budget_shifts?.length > 0 && (
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold font-['Manrope']">Budget Shift Recommendations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {campaigns.budget_shifts.map((shift, i) => (
                          <div key={i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <span className="text-sm font-medium text-red-600 capitalize">{shift.from_source?.replace(/_/g, ' ')}</span>
                            <ArrowRight className="w-4 h-4 text-slate-400 shrink-0" />
                            <span className="text-sm font-medium text-emerald-600 capitalize">{shift.to_source?.replace(/_/g, ' ')}</span>
                            <span className="text-xs text-slate-500 ml-auto">{stripMarkdown(shift.reason)}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Source performance data */}
                {campaigns.source_data?.length > 0 && (
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold font-['Manrope']">Source Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={campaigns.source_data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis dataKey="source" tick={{ fontSize: 12 }} />
                          <YAxis />
                          <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                          <Bar dataKey="total" fill="#0F172A" name="Total" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="booked" fill="#059669" name="Booked" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="bg-white border-slate-200">
                <CardContent className="py-12 text-center">
                  <Button onClick={fetchCampaigns} disabled={campaignLoading} data-testid="load-campaigns-btn">
                    <Lightbulb className="w-4 h-4 mr-2" /> Generate Campaign Recommendations
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default IntelligencePage;
